import React, { Dispatch, SetStateAction, useState } from 'react';
import {
    DatePicker,
    DatePickerProps,
    Dropdown,
    Input,
    MenuProps,
    Select
} from 'antd';
import { Beneficiary } from '../../../interfaces/Beneficiary';
import Button from '../../../components/button/Button';
import CustomModal from '../../../components/CustomModal';
import { FilterIcon } from '../../../components/icons';
import { FilterParams } from '../../../interfaces/Filter';

interface FilterProps {
    setTransactionFilterOptions: Dispatch<SetStateAction<FilterParams>>;
    beneficiaries?: Beneficiary[];
}

const FilterTransactions = ({
    setTransactionFilterOptions,
    beneficiaries
}: FilterProps) => {
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);

    const [tempFilterOptions, setTempFilterOptions] = useState<FilterParams>({
        page: 0,
        size: 10
    });

    const onChangeFrom: DatePickerProps['onChange'] = (date) => {
        setTempFilterOptions({
            ...tempFilterOptions,
            createdDateFrom: date?.format('YYYY-MM-DD HH:mm:ss')
        });
    };

    const onChangeTo: DatePickerProps['onChange'] = (date) => {
        setTempFilterOptions({
            ...tempFilterOptions,
            createdDateTo: date?.format('YYYY-MM-DD HH:mm:ss')
        });
    };

    const handleMenuClick = () => {
        setTransactionFilterOptions(tempFilterOptions);
        setDropdownVisible(false);
        setModalVisible(false);
    };

    const statusOptions = [
        { label: 'COMPLETED', value: '1' },
        { label: 'INITIATED', value: '9' },
        { label: 'CANCELLED', value: '0' },
        { label: 'PROCESSING', value: '5,6,7,8' }
    ];

    const items = [
        {
            key: '1',
            label: <p className='text-ebony text-base'>Filter</p>
        },
        {
            key: '2',
            label: (
                <div>
                    <label className='text-sm text-steelBlue  block mb-2'>
                        Amount Range
                    </label>
                    <div className='flex items-center gap-[12px]'>
                        <Input
                            type='number'
                            value={tempFilterOptions?.amountRangeStart}
                            style={{ height: '40px' }}
                            placeholder='Start amount'
                            onChange={(e) =>
                                setTempFilterOptions({
                                    ...tempFilterOptions,
                                    amountRangeStart: Number(e.target.value)
                                })
                            }
                        />
                        <Input
                            type='number'
                            value={tempFilterOptions?.amountRangeEnd}
                            style={{ height: '40px' }}
                            placeholder='End amount'
                            onChange={(e) =>
                                setTempFilterOptions({
                                    ...tempFilterOptions,
                                    amountRangeEnd: Number(e.target.value)
                                })
                            }
                        />
                    </div>
                </div>
            )
        },
        {
            key: '3',
            label: (
                <div className='mt-[16px] lg:mt-0'>
                    <label className='text-sm text-steelBlue  block mb-2'>
                        Beneficiary
                    </label>
                    <Select
                        style={{ width: '100%' }}
                        value={tempFilterOptions?.corporateBeneficiaryId}
                        onChange={(value: string) =>
                            setTempFilterOptions({
                                ...tempFilterOptions,
                                corporateBeneficiaryId: value
                            })
                        }
                        options={beneficiaries?.map((item: Beneficiary) => ({
                            value: item.id,
                            label: item.beneficiaryCompanyName
                        }))}
                        size='large'
                    />
                </div>
            )
        },
        {
            key: '4',

            label: (
                <div className='mt-[16px] lg:mt-0'>
                    <label className='text-sm text-steelBlue  block mb-2'>
                        Created Date
                    </label>
                    <div className='flex items-center gap-[12px]'>
                        <DatePicker
                            placeholder='Start Date'
                            onChange={onChangeFrom}
                            showTime={{ format: 'HH:mm' }}
                            format='YYYY-MM-DD'
                            style={{ width: '100%', height: '40px' }}
                        />

                        <DatePicker
                            showTime={{ format: 'HH:mm' }}
                            format='YYYY-MM-DD'
                            placeholder='End Date'
                            onChange={onChangeTo}
                            style={{ width: '100%', height: '40px' }}
                        />
                    </div>
                </div>
            )
        },
        {
            key: '5',
            label: (
                <div className='mt-[16px] lg:mt-0'>
                    <label className='text-sm text-steelBlue block mb-2'>
                        Status
                    </label>

                    <Select
                        className='h-[40px] w-full'
                        // value={tempFilterOptions?.status}
                        onChange={(value: string) =>
                            setTempFilterOptions({
                                ...tempFilterOptions,
                                status: value
                            })
                        }
                        options={statusOptions}
                        size='large'
                    />
                </div>
            )
        },
        {
            key: '6',
            label: (
                <div className='flex justify-between py-4'>
                    <Button
                        text='Cancel'
                        onClick={() => setDropdownVisible(false)}
                        variant='secondaryOutline'
                    />
                    <div className='flex gap-[24px]'>
                        <Button
                            text='Reset'
                            onClick={() => {
                                setTransactionFilterOptions({
                                    page: 0,
                                    size: 0
                                });
                                setTempFilterOptions({
                                    page: 0,
                                    size: 0
                                });
                            }}
                            variant='tertiary'
                        />
                        <Button text='Apply Filter' onClick={handleMenuClick} />
                    </div>
                </div>
            )
        }
    ];

    const handleButtonClick = () => {
        setDropdownVisible(!dropdownVisible);
    };

    const dropdownItems = items as MenuProps['items'];

    return (
        <>
            <div className='hidden lg:block'>
                <Dropdown
                    menu={{
                        items: dropdownItems,
                        onMouseLeave: (): void => setDropdownVisible(false)
                    }}
                    placement='bottom'
                    autoAdjustOverflow={false}
                    trigger={['click']}
                    open={dropdownVisible}
                    overlayStyle={{
                        pointerEvents: 'auto'
                    }}
                    dropdownRender={(menu) => (
                        <div className='w-[375px] h-[467px]'>
                            {React.cloneElement(menu as React.ReactElement)}
                        </div>
                    )}
                >
                    <Button
                        leftIcon={<FilterIcon />}
                        text='Filter'
                        variant='secondaryOutline'
                        onClick={handleButtonClick}
                        size='sm'
                    />
                </Dropdown>
            </div>

            <div className='lg:hidden w-[100vw]'>
                <CustomModal
                    isModalOpen={modalVisible}
                    onClose={() => setModalVisible(false)}
                    hideCloseIcon
                >
                    <div className='w-[85vw] h-[45vh]'>
                        {items?.map((item) => item?.label)}
                    </div>
                </CustomModal>

                <Button
                    leftIcon={<FilterIcon />}
                    text='Filter'
                    variant='tertiary'
                    onClick={() => setModalVisible(true)}
                    size='sm'
                />
            </div>
        </>
    );
};

export default FilterTransactions;
